import React, { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../login/logincss/Login.css";
import auravitLogo from "../../images/auravit_logo.png";
import { auth, db } from "../../firebase";
import { signOut } from "firebase/auth";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import {
  clearPendingAccountReactivation,
  clearStoredLoginOtpChallenge,
  finalizePendingAccountReactivation,
  getPendingAccountReactivation,
  getStoredLoginOtpChallenge,
  refreshStoredLoginOtpChallenge,
  verifyStoredLoginOtp,
  setStaff2FAVerified,
  isStaff2FAVerified,
} from "../../services/authFlowService";
import { createOtpRequestId, generateOtpCode, sendOtpEmail, sendOtpSms } from "../../services/otpService";
import { sessionService } from "../../services/sessionService";
import { clearStaffPendingLogin } from "../../services/staffPendingLoginService";

export default function StaffVerification() {
  const location = useLocation();
  const navigate = useNavigate();
  const emailDisplay =
    location.state?.email || location.state?.otpEmail || "your registered email";

  // If already 2FA verified and coming from role selection, go to dashboard
  useEffect(() => {
    if (isStaff2FAVerified() && location.state?.afterStaffOtp) {
      const roleList =
        Array.isArray(location.state?.roles) && location.state.roles.length
          ? location.state.roles
          : Array.isArray(location.state?.user?.roles) && location.state.user.roles.length
          ? location.state.user.roles
          : location.state?.role
          ? [location.state.role]
          : [];
      const selectedRole = location.state?.role || roleList[0] || "Staff";
      
      localStorage.setItem("staffRole", selectedRole);
      const roleRoute = (() => {
        if (selectedRole === "Content Curator") return "/content-curator";
        if (selectedRole === "Medical Professional") return "/medical-professional";
        if (selectedRole === "Peer Educator") return "/peer-educator";
        return "/user-home";
      })();
      navigate(roleRoute, { replace: true, state: { user: location.state?.user, role: selectedRole } });
      sessionStorage.removeItem('freshLoginInProgress');
    }
  }, [location.state, navigate]);

  const OTP_LENGTH = 6;
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [otpExpired, setOtpExpired] = useState(false);
  const [resendText, setResendText] = useState("Resend");
  const [isResending, setIsResending] = useState(false);
  const inputsRef = useRef([]);
  const [verificationMethod, setVerificationMethod] = useState("email");
  const [hasSmsBeenSent, setHasSmsBeenSent] = useState(false);
  const [userPhone, setUserPhone] = useState(null);
  const [timerActive, setTimerActive] = useState(true);
  const [cameFromSmsMethod, setCameFromSmsMethod] = useState(false);
  const [hasEmailBeenSent, setHasEmailBeenSent] = useState(false);

  const normalizePhoneNumber = (phone) => {
    if (!phone) return null;
    const cleaned = String(phone).replace(/\D/g, '');
    
    if (cleaned.length === 10 && cleaned.startsWith('9')) {
      return `+63${cleaned}`;
    } else if (cleaned.length === 11 && cleaned.startsWith('0')) {
      return `+63${cleaned.slice(1)}`;
    } else if (cleaned.length === 12 && cleaned.startsWith('63')) {
      return `+${cleaned}`;
    } else if (phone.startsWith('+63')) {
      return phone;
    }
    return null;
  };

  useEffect(() => {
    if (location.state?.user) {
      const user = location.state.user;
      const rawPhone = user.phone || user.contact || (user.phoneLast10 ? `+63${user.phoneLast10}` : null);
      const phone = normalizePhoneNumber(rawPhone);
      setUserPhone(phone);
    }
  }, [location.state?.user]);

  useEffect(() => {
    const ch = getStoredLoginOtpChallenge();
    if (ch?.otp) {
      const at = String(location.state?.user?.accountType || "").toLowerCase();
      const em = String(location.state?.email || "").toLowerCase();
      if (
        location.state?.staffDeferredLogin ||
        at === "staff" ||
        em.endsWith("@staff.auravit.com")
      ) {
        // Staff OTP verification initiated
      }
    }
  }, [
    location.state?.email,
    location.state?.user?.accountType,
    location.state?.staffDeferredLogin,
  ]);

  useEffect(() => {
    if (!timerActive || timeLeft <= 0) {
      if (timeLeft <= 0) {
        setOtpExpired(true);
      }
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setOtpExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, timerActive]);

  const formatTime = (sec) => {
    const minutes = Math.floor(sec / 60);
    const seconds = sec % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const isOtpComplete = otp.every((digit) => digit !== "");

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError("");

    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData("text").trim().slice(0, OTP_LENGTH);
    if (/^\d+$/.test(pasteData)) {
      const pasteArray = pasteData.split("");
      const newOtp = Array(OTP_LENGTH).fill("");
      pasteArray.forEach((char, i) => {
        if (i < OTP_LENGTH) newOtp[i] = char;
      });
      setOtp(newOtp);
      setError("");
      const lastFilled = pasteArray.length - 1;
      inputsRef.current[Math.min(lastFilled, OTP_LENGTH - 1)].focus();
    }
    e.preventDefault();
  };

  const handleVerify = async () => {
    if (!isOtpComplete) {
      setError("Please enter all 6 digits");
      // Reset OTP boxes when incomplete
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
      return;
    }

    if (otpExpired) {
      setError("Code has expired. Please request a new code.");
      // Reset OTP boxes when expired
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
      return;
    }

    setIsVerifying(true);

    try {
      let result;
      try {
        result = verifyStoredLoginOtp(otp.join(""));
      } catch (verifyErr) {
        // If verification fails due to expired session, create new challenge
        setError("Session expired. A new code has been sent to your email.");
        
        // Auto-trigger resend to create new OTP
        handleResend();
        return;
      }
      
      if (!result.success) {
        setError(result.error);
        // Reset OTP boxes when incorrect
        setOtp(Array(OTP_LENGTH).fill(""));
        inputsRef.current[0]?.focus();
        return;
      }

      setStaff2FAVerified(); // Mark as 2FA verified for this session
      clearStoredLoginOtpChallenge();

      const pendingReactivation = getPendingAccountReactivation();
      if (pendingReactivation?.uid || pendingReactivation?.email) {
        try {
          await finalizePendingAccountReactivation({
            uid: pendingReactivation?.uid || auth.currentUser?.uid,
            email: pendingReactivation?.email || auth.currentUser?.email,
            userName: pendingReactivation?.userName || "Staff",
            db,
            apiBase: process.env.REACT_APP_FUNCTIONS_URL || "https://us-central1-auravit-bb843.cloudfunctions.net",
          });
        } catch (error) {
          console.error("Error finalizing pending reactivation after staff login verification:", error);
        }
      }

      // Update lastLogin timestamp after successful OTP verification
      try {
        const user = auth.currentUser;
        if (user) {
          await updateDoc(doc(db, 'users', user.uid), {
            lastLogin: serverTimestamp()
          });
          // Create a session record for this browser/device
          await sessionService.trackSession(user.uid);
          // Clean up old revoked sessions in the background
          sessionService.pruneOldSessions(user.uid).catch(() => {});
        }
      } catch (error) {
        console.error("Error updating lastLogin after OTP verification:", error);
        // Non-fatal error, continue with login flow
      }

      // Normal login flow (non-password change)
      const roleList =
        Array.isArray(location.state?.roles) && location.state.roles.length
          ? location.state.roles
          : Array.isArray(location.state?.user?.roles) && location.state.user.roles.length
          ? location.state.user.roles
          : location.state?.role
          ? [location.state.role]
          : [];
      const selectedRole = location.state?.role || roleList[0] || "Staff";

      localStorage.setItem("staffRole", selectedRole);
      
      // Navigate directly to dashboard, skip role selection (since we already passed that)
      const roleRoute = (() => {
        if (selectedRole === "Content Curator") return "/content-curator";
        if (selectedRole === "Medical Professional") return "/medical-professional";
        if (selectedRole === "Peer Educator") return "/peer-educator";
        return "/user-home";
      })();

      navigate(roleRoute, { replace: true, state: { user: location.state?.user, role: selectedRole } });
      sessionStorage.removeItem('freshLoginInProgress');
    } catch (err) {
      setError("Unable to continue. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const resetOtpExpiration = () => {
    setOtpExpired(false);
    setTimeLeft(60);
    setTimerActive(true);
  };

  const handleResend = async () => {
    if (resendText === "Sent!" || isResending) return;

    try {
      setIsResending(true);
      setIsVerifying(false);
      setError("");

      let refreshedChallenge;
      try {
        refreshedChallenge = refreshStoredLoginOtpChallenge();
      } catch (refreshErr) {
        // If the session expired, create a new OTP challenge
        
        // Get user info from location state or use defaults for staff
        const email = location.state?.email || "staff@staff.auravit.com";
        const role = location.state?.role || "staff";
        const roles = location.state?.roles || ["staff"];
        const route = location.state?.flow || "password-change";
        
        // Import the create function
        const { createStoredLoginOtpChallenge } = await import("../../services/authFlowService");
        
        refreshedChallenge = createStoredLoginOtpChallenge({
          email,
          role,
          roles,
          route,
          keepSignedIn: false
        });
      }

      if (verificationMethod === "email") {
        const deliveryEmail = refreshedChallenge.otpDeliveryEmail || refreshedChallenge.email;
        
        await sendOtpEmail({
          email: deliveryEmail,
          otp: refreshedChallenge.otp,
          purpose: "login",
          requestId: refreshedChallenge.requestId,
          expiresAt: refreshedChallenge.expiresAt,
        });
      } else if (verificationMethod === "sms" && userPhone) {
        await sendOtpSms({
          phone: userPhone,
          otp: refreshedChallenge.otp,
          purpose: "login",
          requestId: createOtpRequestId("staff-login-sms"),
          expiresAt: refreshedChallenge.expiresAt,
        });
      }

      setResendText("Sent!");
      setOtp(Array(OTP_LENGTH).fill(""));
      resetOtpExpiration();
      if (verificationMethod === "sms") {
        setHasSmsBeenSent(true);
      }
      inputsRef.current[0]?.focus();
      setTimeout(() => setResendText("Resend"), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsResending(false);
    }
  };

  const handleTryAnotherWay = () => {
    setVerificationMethod("sms");
    setHasSmsBeenSent(false);
    setOtp(Array(OTP_LENGTH).fill(""));
    setTimeLeft(60);
    setOtpExpired(false);
    setTimerActive(false);
    setCameFromSmsMethod(false);
  };

  const handleTryAnotherWayBack = () => {
    setVerificationMethod("email");
    setHasSmsBeenSent(false);
    setHasEmailBeenSent(false);
    setOtp(Array(OTP_LENGTH).fill(""));
    setTimeLeft(60);
    setOtpExpired(false);
    setTimerActive(false);
    setCameFromSmsMethod(true);
  };

  const handleSendEmail = async () => {
    if (isResending) return;

    try {
      setIsResending(true);

      const refreshedChallenge = refreshStoredLoginOtpChallenge();

      await sendOtpEmail({
        email: location.state?.email || location.state?.otpEmail || "your registered email",
        otp: refreshedChallenge.otp,
        purpose: "login",
        requestId: createOtpRequestId("staff-login-email"),
        expiresAt: refreshedChallenge.expiresAt,
      });

      setResendText("Sent!");
      setOtp(Array(OTP_LENGTH).fill(""));
      resetOtpExpiration();
      setTimerActive(true);
      setHasEmailBeenSent(true);
      inputsRef.current[0]?.focus();
      setTimeout(() => setResendText("Resend"), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsResending(false);
    }
  };

  const handleSendSms = async () => {
    if (isResending) return;

    try {
      setIsResending(true);

      const refreshedChallenge = refreshStoredLoginOtpChallenge();
      
      await sendOtpSms({
        phone: userPhone,
        otp: refreshedChallenge.otp,
        purpose: "login",
        requestId: createOtpRequestId("staff-login-sms"),
        expiresAt: refreshedChallenge.expiresAt,
      });

      setError("");
      setOtp(Array(OTP_LENGTH).fill(""));
      setTimeLeft(60);
      setOtpExpired(false);
      setTimerActive(true);
      setHasSmsBeenSent(true);
    } catch (e) {
      setError("Unable to send OTP SMS at the moment.");
    } finally {
      setIsResending(false);
    }
  };

  const handleCancel = async () => {
    clearStoredLoginOtpChallenge();
    clearStaffPendingLogin();
    localStorage.removeItem("user");
    sessionStorage.removeItem('freshLoginInProgress');
    try {
      await signOut(auth);
    } catch (err) {
      // Sign-out error is not critical, continue with navigation
    }
    navigate("/login");
  };

  return (
    <div>
      <div className="verification-logo-container">
        <img
          src={auravitLogo}
          alt="AURAVIT"
          className="verification-logo"
        />
      </div>

      <div className="verification-container">
        <div className="verification-card">
          <img
            src={auravitLogo}
            alt="AURAVIT"
            className="verification-logo-image"
          />
          
          <h1 className="verification-title">Enter Verification Code</h1>
        <p className="verification-subtitle">
          Enter 6-digit code we sent to: <strong>{verificationMethod === "sms" ? userPhone : emailDisplay}</strong>
        </p>

        <div 
          className={`verification-otp-inputs ${error ? "shake" : ""}`}
        >
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputsRef.current[index] = el)}
              type="text"
              maxLength="1"
              value={digit}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              className={`verification-otp-input ${error ? "error" : ""}`}
              disabled={isVerifying}
              autoFocus={index === 0}
            />
          ))}
        </div>

        {error && <div className="verification-error-text">{error}</div>}

        {otpExpired && (
          <div className="verification-error-text">Code has expired. Please request a new one.</div>
        )}

        {timerActive && (
          <p className="verification-timer-section">
            Code expires in{" "}
            <span className={`verification-timer ${otpExpired ? "expired" : ""}`}>
              {formatTime(timeLeft)}
            </span>
          </p>
        )}

        {verificationMethod === "email" ? (
          <p className="verification-resend-section">
            {cameFromSmsMethod ? (
              hasEmailBeenSent ? (
                <>
                  Didn't receive a code?{" "}
                  <span
                    className={`verification-resend-link ${resendText === "Sent!" || isResending ? "disabled" : ""}`}
                    onClick={handleResend}
                  >
                    {resendText}
                  </span>
                </>
              ) : (
                <span className={`verification-resend-link ${isResending ? "disabled" : ""}`} onClick={handleSendEmail} style={{ pointerEvents: isResending ? "none" : "auto", opacity: isResending ? 0.5 : 1 }}>Send Email</span>
              )
            ) : (
              <>
                Didn't receive a code?{" "}
                <span
                  className={`verification-resend-link ${resendText === "Sent!" || isResending ? "disabled" : ""}`}
                  onClick={handleResend}
                >
                  {resendText}
                </span>
              </>
            )}
          </p>
        ) : (
          <p className="verification-resend-section">
            {hasSmsBeenSent ? (
              <>Didn't receive a code?{" "}
              <span className={`verification-resend-link ${isResending ? "disabled" : ""}`} onClick={handleResend} style={{ pointerEvents: isResending ? "none" : "auto", opacity: isResending ? 0.5 : 1 }}>{resendText}</span></>
            ) : (
              <span className={`verification-resend-link ${isResending ? "disabled" : ""}`} onClick={handleSendSms} style={{ pointerEvents: isResending ? "none" : "auto", opacity: isResending ? 0.5 : 1 }}>Send SMS</span>
            )}
          </p>
        )}

        {verificationMethod === "sms" && (
          <span className="back-link" onClick={handleTryAnotherWayBack} style={{ textDecoration: "underline" }}>
            Try another way
          </span>
        )}

        {verificationMethod === "email" && userPhone && (
          <span className="back-link" onClick={handleTryAnotherWay} style={{ textDecoration: "underline" }}>
            Try another way
          </span>
        )}

        <button
          className="verification-verify-btn"
          onClick={handleVerify}
          disabled={isVerifying || !isOtpComplete || otpExpired}
        >
          {isVerifying ? "Verifying..." : "Verify"}
        </button>

        <button
          className="verification-cancel-btn"
          onClick={handleCancel}
        >
          Cancel
        </button>
        </div>
      </div>
    </div>
  );
}
